import React, {useState} from "react";
import {ArrowRight, ShieldCheck, CheckCircle2, AlertTriangle, KeyRound, Clock3,
  Building2, BriefcaseBusiness, Workflow, Network, LockKeyhole, BookOpen, Sparkles,
  CircleDollarSign, CalendarDays, Megaphone, MessagesSquare, ClipboardCheck} from "lucide-react";

const PERMISSIONS = [
 {title:"Customer communication",icon:MessagesSquare,systems:"Business email · WhatsApp Business · support inbox",
  minimum:"Read requests, prepare replies and identify urgent cases",
  expanded:"Send approved messages under brand rules and escalation limits",step:"integrations"},
 {title:"Bookings & availability",icon:CalendarDays,systems:"Calendar · booking platform · CRM",
  minimum:"Read schedules, staff capacity, reservations and prices",
  expanded:"Create or amend confirmed reservations with conflict checks",step:"integrations"},
 {title:"Payments & financial truth",icon:CircleDollarSign,systems:"Stripe company · accounting · read-only bank",
  minimum:"Read revenue, invoices, payment status and reconciled cash",
  expanded:"Issue approved invoices/refunds under explicit limits; bank transfers remain a separate mandate",step:"finance"},
 {title:"Marketing & growth",icon:Megaphone,systems:"Meta · Google Ads · social channels · website analytics",
  minimum:"Read performance and prepare campaign proposals",
  expanded:"Publish or adjust campaigns within approved budgets and content rules",step:"integrations"},
 {title:"People & fulfilment",icon:Workflow,systems:"Staff calendar · project management · suppliers",
  minimum:"Read assignments, capacity, work orders and constraints",
  expanded:"Create internal tasks and coordinate delegated supplier work",step:"constitution"},
];

const EXAMPLES = {
 camden:{
    title:"CamdenSiam · photography studio",
    subtitle:"Walk-in studio, vintage sets, sales, photo delivery",
    company:{name:"CamdenSiam",industry:"Photography studio and tourism experiences",
      markets:"Patong, Phuket, Thailand",products:"Vintage gangster photo sessions\nThai vintage photo sessions\nPrivate couples photo sessions",
      currency:"THB",timezone:"Asia/Bangkok"},
    objectives:{goals:"Convert walk-in visitors into paid photo sessions\nDeliver photos on time with high customer satisfaction\nIncrease repeat bookings and profit per session",
      owner_priorities:"Guest consent and privacy\nService quality and honest pricing",
      constraints:"Never publish client photos without consent\nRefund exceptions need manager review\nDo not exceed owner-approved campaign budget"},
 },
 siamluxe:{
   title:"Siam Luxe · premium experiences",
   subtitle:"Boat proposals, sunset cruises, dining and photography",
   company:{name:"Siam Luxe",industry:"Premium tourism and romantic experiences",
      markets:"Phuket, Thailand",products:"Private sunset cruise\nRomantic proposal package\nPrivate dinner and photography",
      currency:"THB",timezone:"Asia/Bangkok"},
   objectives:{goals:"Grow profitable confirmed bookings\nMaintain high guest satisfaction\nReduce manual coordination workload",
      owner_priorities:"Safety of guests and crew\nReliable service fulfilment",
      constraints:"Captain has final authority on sea safety\nNo booking without checked availability\nNo payment or contract without delegated approval"},
 }
};
const safePercent=n=>Math.max(0,Math.min(100,Number(n)||0));

export default function HandoverGuide({handover,company,onStep,onTemplate,onRefresh,onOpenCEO}){
 const [example,setExample]=useState("camden");
 const [showPermissions,setShowPermissions]=useState(true);
 const snapshot=handover||{};
 const stages=snapshot.stages||[];
 const tpl=EXAMPLES[example];
 return <div className="handover-root">
   <div className="handover-hero">
     <div className="handover-eyebrow"><Sparkles size={15}/> ARC GLOBAL · OWNER HANDOVER STUDIO</div>
     <h1>Hand over the work.<br/><em>Keep the authority.</em></h1>
     <p>Tell ARC how your business works, connect the systems it is allowed to use,
       then set the boundaries of its executive role. The same process works for a
       studio in Phuket, a company in London, or a growing international group.</p>
     <div className="handover-hero-actions">
       <button className="handover-gold-btn" onClick={()=>onStep("company")}>
         Start company setup <ArrowRight size={16}/></button>
       <button className="handover-secondary" onClick={onRefresh}>Check my progress</button>
       {onOpenCEO && (snapshot.internal_ceo_cycles||0)>0 && <button className="handover-secondary" onClick={onOpenCEO}>
         Open my CEO Operating Room <ArrowRight size={15}/>
       </button>}
     </div>
     <div className="handover-hero-trust"><ShieldCheck size={16}/>
       Your company data stays separate. Every real-world permission must be explicitly granted,
       tested and revocable. The FULL autonomy preference never bypasses the Action Firewall.
     </div>
   </div>

   <div className="handover-statbar">
     <div><span>OWNER INPUT COMPLETENESS</span><b>{safePercent(snapshot.setup_percent)}%</b>
       <div className="handover-meter"><span style={{width:safePercent(snapshot.setup_percent)+"%"}}/></div>
       <small>{snapshot.setup_complete||0} of {snapshot.setup_total||8} setup areas completed · not an autonomy score</small></div>
     <div><span>CEO INTERNAL PLANNING</span><b>{(snapshot.internal_ceo_cycles||0)>0?"STARTED":"AWAITING DATA"}</b>
       <small>{snapshot.internal_ceo_cycles||0} recorded cycle(s) · {snapshot.internal_ceo_tasks||0} planned task(s) · no external execution</small></div>
     <div><span>VERIFIED PRODUCTION CONNECTORS</span><b>{(snapshot.verified_live_integrations||[]).length}</b>
       <small>Saved credentials ≠ verified active connections</small></div>
   </div>

   <div className="handover-section-head">
     <div><span>01 / HANDOVER IN PLAIN LANGUAGE</span><h2>Eight short steps. One controlled CEO.</h2>
       <p>Each step opens the existing secure company setup and saves into your own organisation.</p></div>
   </div>
   <div className="handover-step-grid">
     {stages.map((s,i)=><button type="button" key={s.id} className={"handover-step "+(s.complete?"done":"waiting")}
        onClick={()=>onStep(s.id)}>
       <div className="handover-step-top"><span>{String(i+1).padStart(2,"0")}</span>
         <span className={"handover-step-state "+(s.complete?"done":"")}>
           {s.complete?<><CheckCircle2 size={13}/> Saved</>:<><Clock3 size={13}/> Needed</>}
         </span></div>
       <h3>{s.title}</h3><p>{s.client_gives}</p>
       <span className="handover-open">Open step <ArrowRight size={14}/></span>
     </button>)}
   </div>

   <div className="handover-playbook">
     <div><span>YOUR BUSINESS KNOWLEDGE</span><h3>Give ARC the actual operating manual</h3>
       <p>Opening hours, real prices, booking and refund rules, common customer questions, staff responsibilities, service delivery, brand voice and emergencies. Without these, ARC must not invent answers or prices.</p>
       <button className="handover-text-btn" onClick={()=>onStep("company")}>Complete CEO playbook <ArrowRight size={14}/></button></div>
     <div className="handover-playbook-items">
       <div className="handover-playbook-count">{snapshot.operating_playbook?.complete||0} / {snapshot.operating_playbook?.total||8} <small>documented</small></div>
       {(snapshot.operating_playbook?.items||[
         {id:"hours",title:"Opening hours"},{id:"prices",title:"Services and prices"},
         {id:"faq",title:"Customer FAQ"},{id:"booking",title:"Booking and refunds"},
         {id:"team",title:"Team roles"},{id:"sop",title:"Delivery procedures"},
         {id:"brand",title:"Brand voice"},{id:"risk",title:"Emergency rules"},
       ]).map(item=><span key={item.id} className={item.complete?"complete":""}>
         {item.complete?<CheckCircle2 size={13}/>:<Clock3 size={13}/>} {item.title}
       </span>)}
     </div>
   </div>

   <div className="handover-section-head">
     <div><span>02 / REAL ACCESS · NOT SHARED PASSWORDS</span><h2>What ARC needs permission to do</h2>
       <p>Start with read-only scopes. Approve execution separately when tests and company policy allow it.</p></div>
     <button className="handover-secondary" onClick={()=>setShowPermissions(x=>!x)}>
       {showPermissions?"Hide details":"Show access details"}
     </button>
   </div>
   {showPermissions&&<div className="handover-permission-grid">
     {PERMISSIONS.map(item=><div className="handover-permission" key={item.title}>
       <div className="handover-permission-title"><item.icon size={19}/><b>{item.title}</b></div>
       <p className="handover-systems">{item.systems}</p>
       <div className="handover-scope"><span>FIRST — OBSERVE</span><p>{item.minimum}</p></div>
       <div className="handover-scope extended"><span>LATER — DELEGATE</span><p>{item.expanded}</p></div>
       <button className="handover-text-btn" onClick={()=>onStep(item.step)}>Configure safely <ArrowRight size={14}/></button>
     </div>)}
   </div>}

   <div className="handover-section-head">
     <div><span>03 / SHOW ME A REAL EXAMPLE</span><h2>What if I handed over CamdenSiam today?</h2>
       <p>Choose an illustrative business. The preview fills no financial numbers and connects no accounts.</p></div>
   </div>
   <div className="handover-example">
     <div className="handover-example-main">
       <div className="handover-tabs">{Object.entries(EXAMPLES).map(([key,item])=>
         <button type="button" key={key} className={example===key?"active":""}
           onClick={()=>setExample(key)}>{item.title.split(" · ")[0]}</button>)}</div>
       <h3>{tpl.title}</h3><p>{tpl.subtitle}</p>
       <div className="handover-example-points">
         <div><span>ARC learns the business</span><b>{tpl.company.industry} · THB · Asia/Bangkok</b></div>
         <div><span>Owner's three priorities</span><b>{tpl.objectives.goals.replaceAll("\n"," · ")}</b></div>
         <div><span>Hard operating rules</span><b>{tpl.objectives.constraints.replaceAll("\n"," · ")}</b></div>
       </div>
       <button className="handover-gold-btn" onClick={()=>onTemplate(example)}>
         Use example as a starting point <ArrowRight size={16}/></button>
       <small>This is a proposed setup template, not a claim that the company or integrations are active.</small>
     </div>
     <div className="handover-example-side">
       <span className="handover-side-kicker">AFTER ONBOARDING</span>
       <h3>What happens next?</h3>
       <div><b>01. Observe</b><p>Import verifiable bookings, invoices, customer requests and operational data.</p></div>
       <div><b>02. Rehearse</b><p>Shadow-run customer replies, schedules, marketing and financial analysis; measure errors.</p></div>
       <div><b>03. Delegate</b><p>Switch on specific, tested actions inside risk, budget and staff approval rules.</p></div>
       <div><b>04. Report</b><p>Receive an owner brief with actions, margins, exceptions and an audit trail.</p></div>
     </div>
   </div>

   <div className="handover-safety">
     <LockKeyhole size={23}/>
     <div><h3>Important: a saved integration is not a working connection.</h3>
       <p>ARC currently supports configuration and internal CEO coordination. Real WhatsApp replies,
         bookings, ads, bank movements and automatic staff actions require commissioned,
         tested adapters. Until then the system must say “not connected”, never pretend it acted.</p>
     </div>
     <button className="handover-secondary" onClick={()=>onStep("integrations")}>Review access <ArrowRight size={14}/></button>
   </div>
 </div>;
}
